import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/24/outline';
import { TFunction } from 'i18next';

import { createHandle, createLoader } from 'services/breadcrumbs/breadcrumbs';
import { ConnectionResponseDTO, useConnection } from 'services/repositories/connections/connections';
import { useCurrentPartner, usePartner } from 'services/repositories/partners/partners';
import { EMPTY_FIELD } from 'support/helpers/const/const';
import { formatDayAndTime } from 'support/helpers/dateTime/dateTime';
import { routeToPage } from 'support/helpers/navigation/navigation';
import { RouteParams } from 'support/helpers/types/types';
import { ConnectionRelationship, ConnectionsRoutes, NetworkRoutes, PartnerDTO } from 'support/types';
import {
  ConnectionNavigationProvider,
  ConnectionOrigin,
} from 'pages/Connections/ConnectionDetailsPage/contexts/ConnectionNavigationContext';
import { NewTradeRequest } from 'pages/TradePartners/NewTradeRequest/NewTradeRequest';
import { ConnectionsActionRequiredBadge } from 'components/ConnectionsActionRequiredBadge/ConnectionsActionRequiredBadge';
import { Button } from 'components/Form/Button/Button';
import { ListTable } from 'components/ListTable/ListTable';
import { TableItem } from 'components/ListTable/types';
import { Page } from 'components/Page/Page';

import { usePartnerToConnectTo } from './hooks/usePartnerToConnectTo';
import { RelationshipBadge } from '../components/RelationshipBadge/RelationshipBadge';
import { NoConnection } from './components/NoConnection/NoConnection';
import { ResendInvitationEmailAlert } from './components/ResendInvitationEmailAlert/ResendInvitationEmailAlert';

type ConnectionDetailsPageParams = RouteParams<typeof ConnectionsRoutes.connectionDetails>;
type NetworkDetailsPageParams = RouteParams<typeof NetworkRoutes.connectionDetails>;
type ConnectionDetailsPageProps = {
  origin: ConnectionOrigin;
};

type PartnerForTradeRequest = Pick<PartnerDTO, 'id' | 'documentTypes' | 'logoUrl' | 'name'>;

export type ConnectionDetailsLocationState = {
  partner?: PartnerForTradeRequest;
};

export const ConnectionDetailsPage = ({ origin }: ConnectionDetailsPageProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { partnerId } = useParams<ConnectionDetailsPageParams>();
  const [openNewTradeRequest, setOpenNewTradeRequest] = useState(false);
  const { data: currentPartner } = useCurrentPartner();

  const locationState = location.state as ConnectionDetailsLocationState | undefined;

  const {
    partner,
    partnerWithRelationships,
    relationships,
    isLoading,
    showNoConnection,
    isWaitingForPartnerOnboarding,
  } = usePartnerToConnectTo(partnerId, {
    partnerForTradeRequest: locationState?.partner,
  });

  const tableHeaders = useMemo(
    () => [
      { key: 'documentType', label: t('connections:partnerConnectionDetails.table.header.documentType') },
      { key: 'status', label: t('connections:partnerConnectionDetails.table.header.status') },
      { key: 'lastUpdated', label: t('connections:partnerConnectionDetails.table.header.lastUpdated') },
      { key: 'actionRequired', label: '' },
    ],
    [t],
  );

  const buildRow = useCallback(
    (relationship: ConnectionRelationship): Array<TableItem> => {
      return [
        {
          key: `documentType-${relationship.id}`,
          element: t(`common:messageTypes.plural.${relationship.message_type}`),
          longText: true,
        },
        {
          key: `status-${relationship.id}`,
          element: <RelationshipBadge relationship={relationship} currentPartnerId={currentPartner?.id || ''} />,
        },
        {
          key: `lastUpdated-${relationship.id}`,
          element: relationship.updated_at ? (
            <span className="tabular-nums">{formatDayAndTime(relationship.updated_at)}</span>
          ) : (
            EMPTY_FIELD
          ),
        },
        {
          key: `actionRequired-${relationship.id}`,
          element: (
            <ConnectionsActionRequiredBadge
              relationships={[relationship]}
              currentPartnerId={currentPartner?.id || ''}
            />
          ),
        },
      ];
    },
    [t, currentPartner?.id],
  );

  if (!partnerId) {
    return null;
  }

  if (showNoConnection && partner) {
    return (
      <ConnectionNavigationProvider origin={origin}>
        <Page isLoading={isLoading}>
          <Page.Head />
          <Page.Section>
            <NoConnection onRequestConnection={() => setOpenNewTradeRequest(true)} />
          </Page.Section>

          <NewTradeRequest
            showPopup={openNewTradeRequest}
            onClose={() => setOpenNewTradeRequest(false)}
            partner={partner}
            connected={false}
          />
        </Page>
      </ConnectionNavigationProvider>
    );
  }

  return (
    <ConnectionNavigationProvider origin={origin}>
      <Page isLoading={isLoading}>
        <Page.Head
          rightContent={
            <Button
              onClick={() => setOpenNewTradeRequest(true)}
              LeftIcon={PlusIcon}
              loading={isLoading}
              analyticsId="connections:new_trade_request"
            >
              {t('connections:partnerConnectionDetails.newTradeRequest')}
            </Button>
          }
        />
        <Page.Section>
          {partnerWithRelationships && isWaitingForPartnerOnboarding && (
            <div className="mb-4">
              <ResendInvitationEmailAlert partner={partnerWithRelationships} />
            </div>
          )}

          <ListTable<ConnectionRelationship>
            headers={tableHeaders}
            data={relationships}
            isLoading={isLoading}
            loadingRowsCount={3}
            rowBuilder={buildRow}
            onRowClick={(relationship) =>
              navigate(
                routeToPage(
                  origin === 'network' ? NetworkRoutes.relationshipDetails : ConnectionsRoutes.relationshipDetails,
                  {
                    partnerId: partnerId,
                    relationshipId: relationship.id,
                  },
                ),
              )
            }
          />
        </Page.Section>

        {partner && (
          <NewTradeRequest
            showPopup={openNewTradeRequest}
            onClose={() => setOpenNewTradeRequest(false)}
            partner={partner}
            connected
          />
        )}
      </Page>
    </ConnectionNavigationProvider>
  );
};

ConnectionDetailsPage.loader = createLoader(
  ({ params }) => useConnection.getKey({ partnerId: params.partnerId }),
  useConnection.queryFn,
);

ConnectionDetailsPage.handle = createHandle<ConnectionResponseDTO | null, ConnectionDetailsPageParams>(
  (params, data) => {
    return {
      label: (_t: TFunction) => data?.data.name ?? '...',
      to: routeToPage(ConnectionsRoutes.connectionDetails, { partnerId: params.partnerId }),
      key: `connection-details-${params.partnerId}`,
    };
  },
);

type NetworkPartnerPageData = PartnerDTO;

ConnectionDetailsPage.loaderFromNetwork = createLoader(
  ({ params }) => usePartner.getKey({ id: params.partnerId }),
  usePartner.queryFn,
);

ConnectionDetailsPage.handleFromNetwork = createHandle<NetworkPartnerPageData, NetworkDetailsPageParams>(
  (params, data) => {
    return {
      label: (_t: TFunction) => data?.name ?? '...',
      to: routeToPage(NetworkRoutes.connectionDetails, { partnerId: params.partnerId }),
      key: `network-partner-${params.partnerId}`,
    };
  },
);
